import React, { useState, useEffect, useMemo, useRef } from 'react';
import DocumentPreview from '../../utils/DocumentPreview';
import GetCookie from "../../utils/GetCookie";
import { useNavigate } from 'react-router-dom';

const DocumentSelector = ({ isOpen, fabricTreeData, selectedDocIds, onDocumentSelect, onClose, sectionName, sectionOrderingId, agentName, agentOrderingId, isEmbedded = false, viewMode = 'collections', onViewModeChange = () => {}, hideViewToggle = false }) => { 
  const [expandedThreads, setExpandedThreads] = useState({});
  const [localSelectedDocIds, setLocalSelectedDocIds] = useState([]);
  
  // Document preview states
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocId, setPreviewDocId] = useState(null);
  const [previewDocumentName, setPreviewDocumentName] = useState(null);


  // Initialize local selection state
  useEffect(() => {
    setLocalSelectedDocIds([...selectedDocIds]);
  }, [selectedDocIds]);
  

  // Normalize classification value — null/"unclassified" → "Unclassified"
  const _normalizeClassification = (cls) => {
    const s = (cls || '').trim();
    if (!s || s.toLowerCase() === 'unclassified') return 'Unclassified';
    return s;
  };

  // Stable hash-based color index 1-7 per classification name
  const getClassificationColorIndex = (name) => {
    if (!name || name === 'Unclassified') return 'unclassified';
    let hash = 0;
    for (let i = 0; i < name.length; i += 1) {
      hash = ((hash << 5) - hash) + name.charCodeAt(i);
      hash |= 0;
    }
    return (Math.abs(hash) % 7) + 1;
  };

  // View 1/3: original threads. View 2: synthetic groups by classification + thread tags.
  const displayedThreads = useMemo(() => {
    if (!fabricTreeData || !fabricTreeData.length) return [];
    const fabric = fabricTreeData[0];
    const threads = fabric.threads || [];

    if (viewMode === 'meta_tags') {
      const namedGroups = new Set();
      let hasUnclassifiedDocs = false;

      threads.forEach(thread => {
        (thread.tags || []).forEach(tag => {
          if (typeof tag === 'string' && tag.trim() && tag.trim().toLowerCase() !== 'unclassified') {
            namedGroups.add(tag.trim());
          }
        });
        (thread.documents || []).forEach(doc => {
          const norm = _normalizeClassification(doc.classification);
          if (norm === 'Unclassified') hasUnclassifiedDocs = true;
          else namedGroups.add(norm);
        });
      });

      const groups = Array.from(namedGroups)
        .sort((a, b) => a.localeCompare(b))
        .map(name => {
          const docs = [];
          threads.forEach(thread => {
            (thread.documents || []).forEach(doc => {
              if (_normalizeClassification(doc.classification) === name) docs.push(doc);
            });
          });
          return { id: `tag-group::${name}`, name, documents: docs, _isTagGroup: true };
        });

      if (hasUnclassifiedDocs) {
        const unclassifiedDocs = [];
        threads.forEach(thread => {
          (thread.documents || []).forEach(doc => {
            if (_normalizeClassification(doc.classification) === 'Unclassified') unclassifiedDocs.push(doc);
          });
        });
        groups.push({ id: 'tag-group::__unclassified__', name: 'Unclassified', documents: unclassifiedDocs, _isTagGroup: true });
      }

      return groups;
    }

    return threads;
  }, [fabricTreeData, viewMode]);

  // Per-thread sub-groups for View 3 (Both)
  const getSubGroupsForThread = (thread) => {
    if (!thread || !thread.documents) return [];
    const namedGroups = new Set();
    let hasUnclassifiedDocs = false;

    (thread.tags || []).forEach(tag => {
      if (typeof tag === 'string' && tag.trim() && tag.trim().toLowerCase() !== 'unclassified') {
        namedGroups.add(tag.trim());
      }
    });
    thread.documents.forEach(doc => {
      const norm = _normalizeClassification(doc.classification);
      if (norm === 'Unclassified') hasUnclassifiedDocs = true;
      else namedGroups.add(norm);
    });

    const subGroups = Array.from(namedGroups)
      .sort((a, b) => a.localeCompare(b))
      .map(name => ({
        id: `${thread.id}::${name}`,
        name,
        documents: thread.documents.filter(doc => _normalizeClassification(doc.classification) === name)
      }));

    if (hasUnclassifiedDocs) {
      subGroups.push({
        id: `${thread.id}::__unclassified__`,
        name: 'Unclassified',
        documents: thread.documents.filter(doc => _normalizeClassification(doc.classification) === 'Unclassified')
      });
    }
    return subGroups;
  };

  // Thread/group selection states — keyed off displayedThreads
  const threadStates = useMemo(() => {
    const states = {};
    displayedThreads.forEach(thread => {
      const threadDocIds = thread.documents?.map(doc => doc.id) || [];
      const selectedCount = threadDocIds.filter(docId => localSelectedDocIds.includes(docId)).length;
      states[thread.id] = {
        isSelected: threadDocIds.length > 0 && selectedCount === threadDocIds.length,
        isPartial: selectedCount > 0 && selectedCount < threadDocIds.length,
        docIds: threadDocIds
      };
    });
    return states;
  }, [displayedThreads, localSelectedDocIds]);

  // Calculate total document count
  const totalDocumentCount = useMemo(() => {
    if (!fabricTreeData || !fabricTreeData.length) return 0;
    const fabric = fabricTreeData[0];
    return fabric.threads?.reduce((count, thread) => {
      return count + (thread.documents?.length || 0);
    }, 0) || 0;
  }, [fabricTreeData]);

  // Groups start collapsed; only a group the user opened (or Expand All) is expanded.
  // Decided in render, with no effect flipping it afterwards, so a view switch
  // doesn't flash one state and then another.
  const isGroupExpanded = (threadId) => expandedThreads[threadId] === true;

  // Toggle thread expansion
  const toggleThreadExpansion = (threadId) => {
    setExpandedThreads(prev => ({
      ...prev,
      [threadId]: !prev[threadId]
    }));
  };

  // Expand / collapse every group in the current view (folders, meta-tag groups, or both)
  const allGroupsExpanded = displayedThreads.length > 0
    && displayedThreads.every(thread => isGroupExpanded(thread.id));
  const toggleExpandAll = () => {
    const next = !allGroupsExpanded;
    setExpandedThreads(Object.fromEntries(displayedThreads.map(thread => [thread.id, next])));
  };

  // Handle thread selection (select/deselect all documents in thread)
  const handleThreadSelection = (threadId, checked) => {
    const threadState = threadStates[threadId];
    if (!threadState) return;

    let newSelection = [...localSelectedDocIds];

    if (checked) {
      // Add all documents from this thread
      threadState.docIds.forEach(docId => {
        if (!newSelection.includes(docId)) {
          newSelection.push(docId);
        }
      });
    } else {
      // Remove all documents from this thread
      newSelection = newSelection.filter(id => !threadState.docIds.includes(id));
    }

    setLocalSelectedDocIds(newSelection);
    if (isEmbedded) {
      onDocumentSelect(newSelection);
    }

  };

  // Handle individual document selection
  const handleDocumentSelection = (docId, checked) => {
    let newSelection = [...localSelectedDocIds];

    if (checked) {
      if (!newSelection.includes(docId)) {
        newSelection.push(docId);
      }
    } else {
      newSelection = newSelection.filter(id => id !== docId);
    }

    setLocalSelectedDocIds(newSelection);
    if (isEmbedded) {
      onDocumentSelect(newSelection);
    }
    
  };

  // Sub-group (View 3) selection state + handler
  const getSubGroupSelectionState = (subGroup) => {
    const docIds = (subGroup.documents || []).map(d => d.id);
    if (docIds.length === 0) return { isSelected: false, isPartial: false, docIds: [] };
    const selectedCount = docIds.filter(id => localSelectedDocIds.includes(id)).length;
    return {
      isSelected: selectedCount === docIds.length,
      isPartial: selectedCount > 0 && selectedCount < docIds.length,
      docIds
    };
  };

  const handleSubGroupSelection = (subGroup, checked) => {
    const docIds = (subGroup.documents || []).map(d => d.id);
    if (docIds.length === 0) return;
    let newSelection;
    if (checked) {
      newSelection = Array.from(new Set([...localSelectedDocIds, ...docIds]));
    } else {
      newSelection = localSelectedDocIds.filter(id => !docIds.includes(id));
    }
    setLocalSelectedDocIds(newSelection);
    if (isEmbedded) onDocumentSelect(newSelection);
  };


  // Select all documents
  const selectAllDocuments = () => {
    const allDocIds = [];
    fabricTreeData.forEach(fabric => {
      fabric.threads?.forEach(thread => {
        thread.documents?.forEach(doc => {
          allDocIds.push(doc.id);
        });
      });
    });
    setLocalSelectedDocIds(allDocIds);
    if (isEmbedded) {
      onDocumentSelect(allDocIds);
    }
   
  };

  // Deselect all documents
  const deselectAllDocuments = () => {
    setLocalSelectedDocIds([]);
    if (isEmbedded) {
      onDocumentSelect([]);
    }
    
  };

  // Enhanced close handler - automatically saves selection
  const handleClose = () => {
    // Auto-save the current selection
    onDocumentSelect(localSelectedDocIds);
    onClose();
  };

  // Handle document preview
  const handlePreviewDocument = (docId, documentName) => {
    setPreviewDocId(docId);
    setPreviewDocumentName(documentName);
    setPreviewOpen(true);
  };

  // Close document preview
  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewDocId(null);
    setPreviewDocumentName(null);
  };

  // Colored pill for a classification name using ev-tag-badge CSS vars
  const renderClassificationBadge = (name) => {
    const idx = getClassificationColorIndex(name);
    const variantClass = idx === 'unclassified' ? 'ev-tag-badge-unclassified' : `ev-tag-badge-${idx}`;
    return <span className={`ev-tag-badge ${variantClass}`}>{name}</span>;
  };

  // View toggle: Collections | Meta Tags | Both
  const renderViewToggle = () => (
    <div className="d-flex align-items-center ms-2">
      <div className="ev-view-toggle-shell">
        <button
          type="button"
          className={`ev-view-toggle-btn ${viewMode === 'collections' ? 'active' : ''}`}
          onClick={() => onViewModeChange('collections')}
        >
          Data Folders
        </button>

        <button
          type="button"
          className={`ev-view-toggle-btn ${viewMode === 'meta_tags' ? 'active' : ''}`}
          onClick={() => onViewModeChange('meta_tags')}
        >
          Meta Tags
        </button>

        <button
          type="button"
          className={`ev-view-toggle-btn ${viewMode === 'both' ? 'active' : ''}`}
          onClick={() => onViewModeChange('both')}
        >
          Both
        </button>
      </div>
    </div>
  );

  if (!isOpen && !isEmbedded) return null;

  const fabric = fabricTreeData[0];
  if (!fabric) {
    return (
      <div className="p-3">
        <div className="ev-alert-card-warning">
          <p className="mb-0">
            <i className="bi bi-info-circle me-1"></i>
            No project selected. Please select a project to view documents.
          </p>
        </div>
      </div>
    );
  }

  const content = (
    <>
      {!isEmbedded && (
        <div className="modal fade modal-high-priority  show d-block bg-dark bg-opacity-50">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content ev-message-card2 d-block ev-card-2">
              <div className="row mb-0 modal-header d-flex align-items-center border-0">
                  <div className="col-11">
                    <span className="ev-reg-p-dark mb-2 d-inline-block text-truncate" style={{ maxWidth: '100%' }} title={fabric.name}>
                      <i className="bi bi-boxes"></i> {fabric.name}
                      <span className="badge ev-message-card2 ms-2 ev-fs-11">
                        Section {sectionOrderingId}. Agent {agentOrderingId}.
                      </span>
                    </span>
                      <button 
                          type="button"
                          className="ev-btn-blue-outline-round ev-bold-reg  ev-btn-small ms-2"
                          onClick={selectAllDocuments}
                        >
                          Select All
                      </button>
                      <button 
                          type="button"
                          className="ev-btn-blue-outline-round ev-bold-reg  ev-btn-small ms-2"
                          onClick={deselectAllDocuments}
                        >
                          Deselect All
                      </button>
                      {/* Document count display instead of manual save button */}
                      <span className="badge bg-primary ms-2 ev-btn-reg">
                        {localSelectedDocIds.length} of {totalDocumentCount} selected
                      </span>
                  </div>
                  <div className="col-1 text-end" >
                    <button 
                      type="button" 
                      className="ev-btn-text-only-fill ev-fs-20"  
                      onClick={handleClose}
                    >
                      <i className="bi bi-x"></i>
                    </button>
                  </div>
              </div>
              <div className="scrollable-element vh-60 ev-padding-small">
                {/* Threads and Documents */}
                <div>
                  {fabric.threads?.map(thread => {
                    const threadState = threadStates[thread.id] || { isSelected: false, isPartial: false };
                    
                    return (
                      <div key={thread.id} className="ev-card-3 mb-2">
                        <div className="d-flex align-items-center">
                          <button 
                            className="btn btn-sm p-0 me-2 w-3" 
                            onClick={() => toggleThreadExpansion(thread.id)}
                          >
                            <i className={`ev-reg-p-dark bi ${isGroupExpanded(thread.id) ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                          </button>
                          
                          <div className="form-check d-flex align-items-center">
                            <input
                              className="form-check-input me-2 position-relative"
                              type="checkbox"
                              checked={threadState.isSelected}
                              onChange={(e) => handleThreadSelection(thread.id, e.target.checked)}
                              id={`thread-${thread.id}`}
                              ref={input => {
                                if (input) {
                                  input.indeterminate = threadState.isPartial;
                                }
                              }}
                            />
                            <label 
                              className="form-check-label ev-small-p-blue avatar-tooltip ev-bold-reg" 
                              data-tooltip={thread.name}
                              htmlFor={`thread-${thread.id}`}
                            >
                              {thread.name.length > 30
                                ? thread.name.substring(0, 30) + '...' 
                              : thread.name}
                            </label>
                            <span className="badge bg-warning ms-2 ev-btn-small">
                              {thread.documents?.length || 0} docs
                            </span> &nbsp;&nbsp;
                            
                          </div>
                        </div>
                        
              
                        
                        {isGroupExpanded(thread.id) && (
                          <div className="mt-2 ms-5">
                            {thread.documents?.map(doc => (
                              <div key={doc.id}>
                              <span className="ev-message-card1 d-inline-flex pe-1 mb-1" style={{ maxWidth: '100%' }}>
                                <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
                                  <input
                                    className="form-check-input me-2 mt-0"
                                    type="checkbox"
                                    checked={localSelectedDocIds.includes(doc.id)}
                                    onChange={(e) => handleDocumentSelection(doc.id, e.target.checked)}
                                    id={`doc-${doc.id}`}
                                    style={{ flexShrink: 0 }}
                                  />
                                  <label
                                    className="form-check-label ev-xsmall-p-blue avatar-tooltip text-truncate"
                                    htmlFor={`doc-${doc.id}`}
                                    data-tooltip={doc.document_name}
                                  >
                                    {doc.document_name}
                                  </label>
                                </div>

                                <button
                                  type="button"
                                  onClick={() => handlePreviewDocument(doc.id, doc.document_name)}
                                  className="ev-btn-lightblue-fill-round ev-btn-xsmall ev-bold-reg ms-2 me-0"
                                  style={{ flexShrink: 0 }}
                                >
                                  View <i className="bi bi-eye"></i>
                                </button>
                              </span>
                              </div>
                            ))}
                            
                            {(!thread.documents || thread.documents.length === 0) && (
                              <p className="ev-small-p-blue text-center mt-2 mb-2">No documents in this Data Folder</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  {(!fabric.threads || fabric.threads.length === 0) && (
                    <div className="text-center p-3">
                      <p className="ev-small-p-blue">No Data folders found in this project</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {isEmbedded && (
        <div className="scrollable-element vh-50 ev-padding-small" >
          {/* Threads and Documents - same as above */}
          <div className="d-flex flex-column mb-2">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <span className="avatar-tooltip-bottom d-inline-block" data-tooltip={fabric.name}>
                  <span className="ev-reg-p-dark d-inline-block text-truncate" style={{ maxWidth: "var(--px-300)" }}>
                  <i className="bi bi-boxes"></i> {fabric.name}

                  {/* Section + Agent Ordering */}
                  {sectionOrderingId && agentOrderingId && (
                    <span className="badge ev-message-card2 ms-2 ev-fs-11">
                      Section {sectionOrderingId}. Agent {agentOrderingId}.
                    </span>
                  )}

                  {/* Section Name */}
                  {sectionName && (
                    <span className="badge bg-info ms-2 ev-fs-11">
                      {sectionName}
                    </span>
                  )}

                  {/* Agent Name */}
                  {agentName && (
                    <span className="badge bg-secondary ms-2 ev-fs-11">
                      {agentName}
                    </span>
                  )}
                </span>
                </span>

                {/* View-mode toggle — right of fabric name */}
                {hideViewToggle !== true && (
                  <div className="ms-3">
                    {renderViewToggle()}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="text-end">
                <button
                  type="button"
                  className="ev-btn-blue-outline-round ev-bold-reg ev-btn-small ms-2"
                  onClick={toggleExpandAll}
                  disabled={displayedThreads.length === 0}
                >
                  <i className={`bi ${allGroupsExpanded ? 'bi-arrows-collapse' : 'bi-arrows-expand'} me-1`}></i>
                  {allGroupsExpanded ? 'Collapse All' : 'Expand All'}
                </button>

                <button
                  type="button"
                  className="ev-btn-blue-outline-round ev-bold-reg ev-btn-small ms-2"
                  onClick={selectAllDocuments}
                >
                  Select All
                </button>

                <button
                  type="button"
                  className="ev-btn-blue-outline-round ev-bold-reg ev-btn-small ms-2"
                  onClick={deselectAllDocuments}
                >
                  Deselect All
                </button>

                <span className="badge bg-primary ms-2 ev-btn-reg">
                  {localSelectedDocIds.length} of {totalDocumentCount} selected
                </span>
              </div>
            </div>
          </div>
          <div>
            {displayedThreads.map(thread => {
              const threadState = threadStates[thread.id] || { isSelected: false, isPartial: false };
              const isTagGroup = !!thread._isTagGroup;
              const subGroups = (viewMode === 'both' && !isTagGroup && isGroupExpanded(thread.id))
                ? getSubGroupsForThread(thread)
                : null;

              return (
                <div key={thread.id} className="mb-2 ev-card-3">
                  <div className="d-flex align-items-center">
                    <button 
                      className="btn btn-sm p-0 me-2 w-3" 
                      onClick={() => toggleThreadExpansion(thread.id)}
                    >
                      <i className={`ev-reg-p-dark bi ${isGroupExpanded(thread.id) ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                    </button>
                    
                    <div className="form-check d-flex align-items-center">
                      <input
                        className="form-check-input me-2 position-relative mt-0"
                        type="checkbox"
                        checked={threadState.isSelected}
                        onChange={(e) => handleThreadSelection(thread.id, e.target.checked)}
                        id={`thread-${thread.id}`}
                        ref={input => {
                          if (input) input.indeterminate = threadState.isPartial;
                        }}
                      />
                      {isTagGroup ? (
                        // View 2: colored classification badge as label
                        <label className="form-check-label ms-1" htmlFor={`thread-${thread.id}`}>
                          {renderClassificationBadge(thread.name)}
                        </label>
                      ) : (
                        // View 1 / View 3: original thread name
                        <label 
                          className="form-check-label ev-small-p-blue avatar-tooltip ev-bold-reg" 
                          data-tooltip={thread.name}
                          htmlFor={`thread-${thread.id}`}
                        >
                          {thread.name.length > 30 ? thread.name.substring(0, 30) + '...' : thread.name}
                        </label>
                      )}
                      <span className="badge bg-warning ms-2 ev-btn-small">
                        {thread.documents?.length || 0} docs
                      </span>
                    </div>
                  </div>

                  <div className="d-flex justify-content-start align-items-center mt-1 mb-1"></div>

                  {/* View 3 (Both): sub-groups by classification inside each thread */}
                  {subGroups && (
                    <div className="ms-5 mt-1">
                      {subGroups.map(subGroup => {
                        const subState = getSubGroupSelectionState(subGroup);
                        return (
                          <div key={subGroup.id} className="mb-2">
                            <div className="form-check d-flex align-items-center">
                              <input
                                className="form-check-input me-2 mt-0"
                                type="checkbox"
                                checked={subState.isSelected}
                                onChange={(e) => handleSubGroupSelection(subGroup, e.target.checked)}
                                id={`subgroup-${subGroup.id}`}
                                disabled={subGroup.documents.length === 0}
                                ref={input => { if (input) input.indeterminate = subState.isPartial; }}
                              />
                              <label className="form-check-label" htmlFor={`subgroup-${subGroup.id}`}>
                                {renderClassificationBadge(subGroup.name)}
                              </label>
                              <span className="badge bg-warning ms-2 ev-btn-small">
                                {subGroup.documents.length} docs
                              </span>
                            </div>
                            {subGroup.documents.length > 0 ?  (
                              <div className="ms-4 mt-1">
                                {subGroup.documents.map(doc => (
                                  <div key={doc.id}>
                                    <span className="ev-message-card1 d-inline-flex pe-1 mb-1" style={{ maxWidth: '100%' }}>
                                      <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
                                        <input
                                          className="form-check-input me-2 mt-0"
                                          type="checkbox"
                                          checked={localSelectedDocIds.includes(doc.id)}
                                          onChange={(e) => handleDocumentSelection(doc.id, e.target.checked)}
                                          id={`doc-${doc.id}`}
                                          style={{ flexShrink: 0 }}
                                        />
                                        <label className="form-check-label ev-xsmall-p-blue text-truncate" htmlFor={`doc-${doc.id}`} title={doc.document_name}>
                                          {doc.document_name}
                                        </label>
                                      </div>
                                      <button type="button" onClick={() => handlePreviewDocument(doc.id, doc.document_name)} className="ev-btn-lightblue-fill-round ev-btn-xsmall ev-bold-reg ms-2 me-0" style={{ flexShrink: 0 }}>
                                        View <i className="bi bi-eye"></i>
                                      </button>
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ):(
                                <p className="ev-small-p-blue ms-5 mt-2 mb-2">No documents found</p>
                              )}
                          </div>
                        );
                      })}
                      {subGroups.length === 0 && (
                        <p className="ev-small-p-blue text-center mt-2 mb-2">No documents in this Data Folder</p>
                      )}
                    </div>
                  )}

                  {/* View 1 / View 2: flat doc list */}
                  {!subGroups && isGroupExpanded(thread.id) && (
                    <div className="mt-2 ms-5">
                      {thread.documents?.map(doc => (
                        <div key={doc.id}>
                          <span className="ev-message-card1 d-inline-flex pe-1 mb-1" style={{ maxWidth: '100%' }}>
                            <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
                              <input
                                className="form-check-input me-2 mt-0"
                                type="checkbox"
                                checked={localSelectedDocIds.includes(doc.id)}
                                onChange={(e) => handleDocumentSelection(doc.id, e.target.checked)}
                                id={`doc-${doc.id}`}
                                style={{ flexShrink: 0 }}
                              />
                              <label
                                className="form-check-label ev-xsmall-p-blue text-truncate"
                                htmlFor={`doc-${doc.id}`}
                                title={doc.document_name}
                              >
                                {doc.document_name}
                              </label>
                            </div>
                            <button
                              type="button"
                              onClick={() => handlePreviewDocument(doc.id, doc.document_name)}
                              className="ev-btn-lightblue-fill-round ev-btn-xsmall ev-bold-reg ms-2 me-0"
                              style={{ flexShrink: 0 }}
                            >
                              View <i className="bi bi-eye"></i>
                            </button>
                          </span>
                        </div>
                      ))}
                      {(!thread.documents || thread.documents.length === 0) && (
                        <p className="ev-small-p-blue text-center mt-2 mb-2">
                          {isTagGroup ? 'No documents in this tag' : 'No documents in this Data Folder'}
                        </p>

                      )}
                    </div>
                  )}
                </div>
              );
            })}
            
            {displayedThreads.length === 0 && (
              <div className="text-center p-3">
                <p className="ev-small-p-blue">No Data folders found in this project</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Document Preview Modal - shared for both modes */}
      <DocumentPreview 
        isOpen={previewOpen}
        onClose={closePreview}
        docId={previewDocId}
        documentName={previewDocumentName}
      />
    </>
  );

  if (!isEmbedded) {
    return content;
  }

  return content;
};

export default DocumentSelector;